class GOCSequenceFrame {
  constructor(options) {
    if (!options) {
      console.log('options is required');
      return;
    }
    if (!options.parentElement) {
      console.log('options.parentElement is required');
      return;
    }
    if (!options.webWorkerScriptPath) {
      console.log('options.webWorkerScriptPath is required');
      return;
    }
    // 设备检测标志
    this.breakpoint = 768;
    this.isPc = this.checkViewportWidth(this.breakpoint);

    // 布局参数
    this.canvasWidth = 0;
    this.canvasHeight = 0;

    // 资源管理
    this.pcImageNum = 0;
    this.mobImageNum = 0;
    this.pcImageUrlList = [];
    this.mobImageUrlList = [];

    this.imgHtmlList = [];
    this.isOnceUpdateImg = true; // 一次性执行控制（防重复执行）

    // 动画控制
    this.previousProgress = 0;
    this.currentFrameIndex = 0;
    this.isScrollingDown = true;
    this.isFirstRender = true; // 首次渲染控制


    // Worker实例
    this.imageLoaderWorker = null;


    // 初始化
    this.container = options.parentElement;
    this.webWorkerScriptPath = options.webWorkerScriptPath;
    this.init();
  }

  // 核心初始化流程
  init() {
    this.setupCanvas();
    this.loadAllImgByWorker();
    this.bindResizeEvents();
  }

  debounce(fn, delay) {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };
  }

  bindResizeEvents() {
    this.resizeHandler = this.debounce(this.handleViewportChange.bind(this), 200);
    window.addEventListener('resize', this.resizeHandler);
  }

  loadAllImgByWorker() {
    if (!window.Worker) {
      console.error('Browser does not support Web Workers');
      return;
    }

    // 如果已有 worker，则先终止并置为 null
    if (this.imageLoaderWorker) {
      this.imageLoaderWorker.terminate();
      this.imageLoaderWorker = null;
    }

    try {

      this.imageLoaderWorker = new Worker(this.webWorkerScriptPath);
      const urlList = this.isPc ? this.pcImageUrlList : this.mobImageUrlList;
      let loadedCount = 0; // 记录加载完成的图片数

      urlList.forEach((url, index) => {
        this.imageLoaderWorker.postMessage({
          url: url,
          index: index
        });
      });

      this.imageLoaderWorker.onmessage = ({ data }) => {
        if (data.Blob) {
          const imageUrl = URL.createObjectURL(data.Blob);
          const img = new Image();
          img.style.objectFit = "cover";
          img.src = imageUrl;
          img.onload = () => {
            this.imgHtmlList[data.index] = img;
            loadedCount++;
            // 确保是单次队列执行
            if (this.imgHtmlList.length === urlList.length && this.isOnceUpdateImg) {
              this.isOnceUpdateImg = false;
              this.renderFirstFrame();
              // console.log('loadAllImgByWorker Done');
            }
            URL.revokeObjectURL(imageUrl);
            // 如果所有图片都加载完成，则终止 worker 并将其置为 null 主动释放内存
            if (loadedCount === urlList.length) {
              this.imageLoaderWorker.terminate();
              this.imageLoaderWorker = null;
            }
          }
        }
      }
    } catch (err) {
      console.error('loadAllImgByWorker Error', err);
    }
  }

  // 渲染首帧
  renderFirstFrame() {
    const currentImageUrlLength = this.isPc ? this.pcImageUrlList.length : this.mobImageUrlList.length;
    if (this.imgHtmlList.length >= currentImageUrlLength && this.isFirstRender) {
      this.isFirstRender = false;

      // 渲染第一帧
      if (!this.ctx) {
        return;
      }
      let frameIndex = 1;
      const image = this.imgHtmlList[frameIndex];
  
      try {
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.ctx.drawImage(image, 0, 0, this.canvasWidth, this.canvasHeight);
        this.currentFrameIndex = frameIndex;
        console.log('renderFirstFrame By Canvas success');
      } catch (err) {
        console.error('renderFirstFrame By Canvas Error', err);
      }
    }
  }

  // 生成图片序列URL
  generateImageUrls(firstImageUrl, totalImages, imageUrlList) {
    if (!Boolean(firstImageUrl)) {
      return;
    }

    const url = new URL(firstImageUrl); // 解析传入的首张图片 URL
    const pathname = url.pathname; // 获取路径名
    const filename = pathname.split("/").pop(); // 获取文件名（如 image1.jpg）

    // 获取文件名前缀（去掉最后的 "1"），如 "image1.jpg" -> "image" 【需要确保首图的文件名以 "1" 结尾】
    const namePrefix = filename.slice(0, filename.lastIndexOf("1"));
    const fileExtension = filename.slice(filename.lastIndexOf(".")); // 获取文件扩展名（如 .jpg）

    // 生成完整的图片 URL 并存入 imageUrlList
    for (let index = 1; index <= totalImages; index++) {
      const imageUrl = `${url.origin}${pathname.slice(0, pathname.lastIndexOf("/") + 1)}${namePrefix}${index}${fileExtension}`;
      imageUrlList.push(imageUrl);
    }
    // console.log('imageUrlList', imageUrlList);
  }

  // 初始化Canvas
  setupCanvas() {
    const canvasElement = this.getCanvasElement();
    if (!canvasElement) return;

    // 设置Canvas尺寸
    const [width, height] = this.calculateCanvasSize(canvasElement);
    this.canvasWidth = width;
    this.canvasHeight = height;

    // 获取绘图上下文
    this.ctx = canvasElement.getContext('2d');

    // 加载图片序列配置
    const {
      pcFirstImageUrl: pcImage1,
      mobFirstImageUrl: mobImage1,
      pcImageNum: pcFrames,
      mobImageNum: mobFrames
    } = canvasElement.dataset;

    const pcImageNumCount = Number(pcFrames);
    const mobImageNumCount = Number(mobFrames);

    this.pcImageNum = pcImageNumCount;
    this.mobImageNum = mobImageNumCount;

    // 生成URL列表
    if (this.isPc) {
      this.generateImageUrls(pcImage1, pcImageNumCount, this.pcImageUrlList);
    } else {
      this.generateImageUrls(mobImage1, mobImageNumCount, this.mobImageUrlList);
    }
  }

  // 渲染单帧到Canvas
  renderFrame(progress) {
    if (!this.ctx) {
      return;
    }

    const isForwardScroll = progress >= this.previousProgress; // 获取当前滚动方向
    const currentImageUrlLength = this.isPc ? this.pcImageUrlList.length : this.mobImageUrlList.length;
    let frameIndex = Math.floor(progress * (currentImageUrlLength - 1));

    // 如果是反向滚动，检查索引是否减少
    if (!isForwardScroll) {
      frameIndex = Math.max(frameIndex, 0);  // 确保不会低于0
    } else {
      frameIndex = Math.min(frameIndex, currentImageUrlLength - 1);  // 确保不会超过总图片数
    }
    const image = this.imgHtmlList[frameIndex];

    if (frameIndex === this.currentFrameIndex || !image) {
      return;
    }

    try {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.ctx.drawImage(image, 0, 0, this.canvasWidth, this.canvasHeight);
      this.previousProgress = progress; // 更新previousProgress，记录本次滚动的进度
      this.currentFrameIndex = frameIndex;

      // console.log('renderFrame By Canvas', frameIndex);
    } catch (err) {
      console.error('renderFrame By Canvas Error', err);
    }
  }

  handleViewportChange() {
    const canvasElement = this.getCanvasElement();
    if (!canvasElement) return;

    const [width, height] = this.calculateCanvasSize(canvasElement);
    this.canvasWidth = width;
    this.canvasHeight = height;

    // isPc状态变更
    const isPc = this.checkViewportWidth(this.breakpoint);
    if (isPc !== this.isPc) {
      // 清除旧图片缓存
      this.imgHtmlList = [];
      this.isOnceUpdateImg = true;
      this.isFirstRender = true;

      this.isPc = isPc;
      this.loadAllImgByWorker();
    }
  }

  // 辅助方法
  checkViewportWidth(breakpoint) {
    return window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  }

  getCanvasElement() {
    return this.container.querySelector(
      this.isPc ? '.image-sequences-canvas-pc' : '.image-sequences-canvas-m'
    );
  }

  calculateCanvasSize(element) {
    return [element.width, element.height];
  }
}